// Sourced, per-EWC-chapter industrial-symbiosis narratives: why the stream
// was historically landfilled/incinerated, and the symbiosis angle that
// changes that. Written once per category, each citing a public reference —
// no @circular self-authored claims, no fabricated entries.
export interface ISNarrative {
  chapter: string;
  barrier: string;
  symbiosisAngle: string;
  source: string;
  sourceUrl: string;
}

export const ISNarratives: Record<string, ISNarrative> = {
  '14': {
    chapter: '14',
    barrier: 'Spent organic solvents were historically incinerated because mixed, water- and particulate-contaminated solvent streams fell below reuse spec and no economical separation existed on-site.',
    symbiosisAngle: 'Fractional distillation recovers a technical-grade solvent for reuse; closed-loop reclamation at the point of generation can take the stream out of waste regulation entirely, leaving only still bottoms.',
    source: '40 CFR 261.4(a)(8) closed-loop reclamation; US EPA solvent recycling guidance',
    sourceUrl: 'https://www.ecfr.gov/current/title-40/chapter-I/subchapter-I/part-261/subpart-A/section-261.4',
  },
  '10': {
    chapter: '10',
    barrier: 'Thermal-process residues such as FGD/red gypsum carried iron and trace-metal loads and variable sulphate chemistry that kept them out of plasterboard and cement gypsum specs, so they were landfilled.',
    symbiosisAngle: 'Where composition meets published recycled-gypsum criteria, the sulphate fraction substitutes for natural or FGD gypsum in cement set-retardation and plasterboard; acceptance is decided against receiver quality criteria, not assumed.',
    source: 'Eurogypsum Quality Criteria for Recycled Gypsum (2024); EN 197-1',
    sourceUrl: 'https://www.eurogypsum.org/',
  },
  '06': {
    chapter: '06',
    barrier: 'Wastes from inorganic chemical processes (spent acids, alkalis, metal salt solutions) were neutralized and landfilled because their contamination profile varied batch-to-batch and no standard offtake spec existed.',
    symbiosisAngle: 'Spent acids and alkalis can be regenerated or used directly as feedstock in other processes (e.g. neutralization reagent, metal-salt recovery) where the receiving process can tolerate the specific contaminant load.',
    source: '40 CFR 261.2(c)(3) Table 1 — reclamation of commercial chemical products; US EPA definition of solid waste',
    sourceUrl: 'https://www.ecfr.gov/current/title-40/chapter-I/subchapter-I/part-261/subpart-A/section-261.2',
  },
  '11': {
    chapter: '11',
    barrier: 'Wastes from chemical surface treatment of metals — spent pickle liquors, plating baths and treatment sludges — were neutralised and landfilled because their strong acid or alkaline matrix and dissolved-metal load made direct reuse hazardous and off-spec.',
    symbiosisAngle: 'Spent pickle liquor from steel finishing is regenerable: acid-recovery (roasting/retardation) returns usable acid while precipitating iron oxide for pigment or ferrite feedstock, and the metal fraction of treatment sludges can route to secondary smelting once the matrix is neutralised to receiver limits.',
    source: '40 CFR 261.32 — K062 spent pickle liquor from steel finishing (SIC 331/332)',
    sourceUrl: 'https://www.ecfr.gov/current/title-40/chapter-I/subchapter-I/part-261/subpart-D/section-261.32',
  },
  '12': {
    chapter: '12',
    barrier: 'Metal shaping and surface-treatment sludges (grinding swarf, machining fines, treatment sludges) were landfilled because entrained oils, coolants and mixed metal fractions blocked direct smelter acceptance.',
    symbiosisAngle: 'De-oiled and screened metal fines and swarf are an established feedstock for secondary smelting once moisture and oil content are brought within smelter intake limits; oily fractions route to used-oil re-refining separately.',
    source: '40 CFR Part 266 Subpart F — recyclable materials utilized for precious/secondary metal recovery',
    sourceUrl: 'https://www.ecfr.gov/current/title-40/chapter-I/subchapter-I/part-266/subpart-F',
  },
};

export function getISNarrative(chapter: string): ISNarrative | null {
  return ISNarratives[chapter] ?? null;
}
